import { defineStore } from 'pinia'

/**
 * Ürün varyasyonlarını yöneten store
 * Varyant seçimlerini, seçili varyasyonu ve ilgili state'leri yönetir
 */
export const useBuyboxVariationsStore = defineStore('buybox-variation', () => {
  /**
   * State Tanımlamaları
   */
  const state = {
    selectedTerms: ref({}),
    infoMessage: ref(''),
    showInfoMessage: ref(false),
    selectedVariation: ref(null),
    variations: ref([])
  }

  /**
   * Getters
   */
  const getters = {
    // Varyant özelliklerini gruplandırır ve tekil hale getirir
    groupedAttributes: computed(() => {
      const groups = {}

      state.variations.value.forEach((variation) => {
        variation.variation_attributes.forEach((attr) => {
          if (!groups[attr.attribute_name]) {
            groups[attr.attribute_name] = new Set()
          }
          groups[attr.attribute_name].add(
            JSON.stringify(attr.product_attribute_term)
          )
        })
      })

      return Object.fromEntries(
        Object.entries(groups).map(([key, value]) => [
          key,
          Array.from(value).map((termStr) => JSON.parse(termStr))
        ])
      )
    }),

    // Seçili varyasyonun stok durumu
    isInStock: computed(
      () => state.selectedVariation.value?.buybox_winner_data.is_in_stock
    ),

    // Seçili varyasyonun maksimum sipariş adedi
    maxQuantity: computed(
      () =>
        state.selectedVariation.value?.buybox_winner_data.stock_quantity || 10
    ),

    // Seçili varyasyonun ID'si
    variationId: computed(
      () => state.selectedVariation.value?.product_variation_id
    ),

    // Seçili varyasyonun satış fiyatı
    salePrice: computed(
      () => state.selectedVariation.value?.buybox_winner_data.sale_price
    ),

    // Seçili varyasyonun normal fiyatı
    originalPrice: computed(
      () => state.selectedVariation.value?.buybox_winner_data.sale_price
    ),

    // Seçili varyasyonun satıcı bilgileri
    vendorInfo: computed(
      () => state.selectedVariation.value?.buybox_winner_data.vendor_info
    ),

    // Seçili varyasyonun stok miktarı
    stockQty: computed(
      () => state.selectedVariation.value?.buybox_winner_data.stock_qty
    )
  }

  /**
   * Mutations - State güncelleme metodları
   */
  const mutations = {
    setVariations: (variations) => {
      state.variations.value = variations
    },

    setSelectedTerms: (terms) => {
      state.selectedTerms.value = terms
    },

    setSelectedVariation: (variation) => {
      state.selectedVariation.value = variation
    },

    showMessage: (message) => {
      state.infoMessage.value = message
      state.showInfoMessage.value = true
      setTimeout(() => {
        state.showInfoMessage.value = false
      }, 3000)
    },

    resetState: () => {
      state.selectedTerms.value = {}
      state.infoMessage.value = ''
      state.showInfoMessage.value = false
      state.selectedVariation.value = null
      state.variations.value = []
    }
  }

  /**
   * Actions - İş mantığı metodları
   */
  const actions = {
    /**
     * Bir terimin mevcut seçimlerle uyumlu olup olmadığını kontrol eder
     */
    isTermAvailable: (attrName, term) => {
      const currentSelection = {
        ...state.selectedTerms.value,
        [attrName]: term
      }

      return state.variations.value.some((variation) => {
        return variation.variation_attributes.every((attr) => {
          if (!currentSelection[attr.attribute_name]) return true
          return (
            currentSelection[attr.attribute_name] ===
            attr.product_attribute_term.name
          )
        })
      })
    },

    /**
     * Seçili varyasyonu günceller
     */
    updateSelectedVariation: () => {
      // Hiç seçim yoksa ilk varyasyonu seç
      if (Object.keys(state.selectedTerms.value).length === 0) {
        mutations.setSelectedVariation(state.variations.value[0])
        return
      }

      // Seçimlerle eşleşen varyasyonu bul
      const variation = state.variations.value.find((variation) => {
        return variation.variation_attributes.every(
          (attr) =>
            state.selectedTerms.value[attr.attribute_name] ===
            attr.product_attribute_term.name
        )
      })

      mutations.setSelectedVariation(variation)
    },

    /**
     * Yeni bir terim seçildiğinde çağrılır
     */
    selectTerm: (attrName, term) => {
      const newSelection = {
        ...state.selectedTerms.value,
        [attrName]: term
      }

      // Seçimlerle uyumlu bir varyasyon var mı kontrol et
      const hasMatchingVariation = state.variations.value.some((variation) =>
        variation.variation_attributes.every(
          (attr) =>
            newSelection[attr.attribute_name] ===
            attr.product_attribute_term.name
        )
      )

      // Uyumlu varyasyon yoksa, diğer özellikleri uyumlu bir varyanta göre güncelle
      if (!hasMatchingVariation) {
        const matchingVariation = state.variations.value.find((variation) =>
          variation.variation_attributes.some(
            (attr) =>
              attr.attribute_name === attrName &&
              attr.product_attribute_term.name === term
          )
        )

        if (matchingVariation) {
          matchingVariation.variation_attributes.forEach((attr) => {
            newSelection[attr.attribute_name] = attr.product_attribute_term.name
          })
          mutations.showMessage(
            'Diğer özellikler uyumlu seçeneklerle güncellendi'
          )
        }
      }

      mutations.setSelectedTerms(newSelection)
      actions.updateSelectedVariation()
    },

    /**
     * Başlangıç varyasyonunu ayarlar
     */
    initializeVariation: () => {
      const selectedVariation = state.variations.value.find(
        (variation) => variation.selected
      )

      if (selectedVariation) {
        const initialTerms = {}
        selectedVariation.variation_attributes.forEach((attr) => {
          initialTerms[attr.attribute_name] = attr.product_attribute_term.name
        })
        mutations.setSelectedTerms(initialTerms)
        mutations.setSelectedVariation(selectedVariation)
      }
    }
  }

  return {
    // Public API
    ...state,
    getGroupedAttributes: getters.groupedAttributes,
    isInStock: getters.isInStock,
    maxQuantity: getters.maxQuantity,
    variationId: getters.variationId,
    salePrice: getters.salePrice,
    originalPrice: getters.originalPrice,
    vendorInfo: getters.vendorInfo,
    stockQty: getters.stockQty,
    setVariations: mutations.setVariations,
    setSelectedTerms: mutations.setSelectedTerms,
    setSelectedVariation: mutations.setSelectedVariation,
    isTermAvailable: actions.isTermAvailable,
    selectTerm: actions.selectTerm,
    initializeVariation: actions.initializeVariation,
    resetState: mutations.resetState
  }
})
